"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConferenceRoom = void 0;
const webrtc_peer_1 = require("./webrtc_peer");
const participant_1 = require("./participant");
const logger_1 = require("./util/logger");
class ConferenceRoom {
    constructor(config, messageHandler) {
        this.messageHandler = messageHandler;
        this.participants = {};
        this.isAudioLive = false;
        this.isVideoLive = false;
        this.receivedIceCandidates = false;
        this.wsPollCount = 0;
        this.iceServers = [];
        this.config = this.wrapDataTrackOnMessage(config);
        this.name = config.userName;
        this.room = config.roomName;
        this.ws = new WebSocket(config.wsUri);
        this.config.configuration = this.config.configuration || {};
        this.config.preferredAudioCodec = this.config.preferredAudioCodec || [];
        this.config.preferredVideoCodec = this.config.preferredVideoCodec || [];
        this.videoStream = this.config.videoStream;
        this.audioStream = this.config.audioStream;
        this.ws.onmessage = (message) => {
            const parsedMessage = JSON.parse(message.data);
            switch (parsedMessage.id) {
                case 'existingParticipants':
                    this.initLocalAndExistingParticipants(parsedMessage);
                    break;
                case 'newParticipantArrived':
                    this.onNewParticipant(parsedMessage);
                    break;
                case 'participantLeft':
                    this.onParticipantLeft(parsedMessage);
                    break;
                case 'receiveVideoAnswer':
                    this.receiveVideoResponse(parsedMessage);
                    break;
                case 'iceCandidate':
                    if (!this.receivedIceCandidates) {
                        this.receivedIceCandidates = true;
                        logger_1.logger.info('[ARaaS] ICE candidates have begun being received.');
                    }
                    this.participants[parsedMessage.name].rtcPeer.addIceCandidate(parsedMessage.candidate, (error) => {
                        if (error) {
                            logger_1.logger.error('[ARaaS] Error adding candidate: ', error);
                        }
                    });
                    break;
                case 'natCredentials':
                    this.parseAndConfigureIceServers(parsedMessage.iceServer);
                    break;
                case 'HeartBeat':
                    // This is not used yet.
                    break;
                case 'stats':
                    console.debug(parsedMessage);
                    break;
                default:
                    logger_1.logger.error('[ARaaS] Unrecognized message', parsedMessage);
            }
        };
        this.ws.onerror = (event) => {
            logger_1.logger.error('[ARaaS] ConferenceRoom websocket has encountered an error: ', event);
        };
        this.ws.onclose = (event) => {
            logger_1.logger.info('[ARaaS] ConferenceRoom websocket has closed: ', event);
        };
    }
    waitForSocketConnection(socket, callback) {
        setTimeout(() => {
            if (socket.readyState === 1) {
                logger_1.logger.info('[ARaaS] Successfully connected to socket');
                if (callback != null) {
                    callback();
                }
            }
            else {
                if (this.wsPollCount >= 200) {
                    logger_1.logger.warn('[ARaaS] Failed to connect to socket');
                    socket.close();
                }
                else {
                    this.wsPollCount++;
                    console.debug('[ARaaS] Attempting to connect to socket');
                    this.waitForSocketConnection(socket, callback);
                }
            }
        }, 50);
    }
    register() {
        logger_1.logger.info(`[ARaaS] Joining room: ${this.room} as user: ${this.name}`);
        this.videoStream = this.removeSuspendedTracks(this.config.videoStream);
        const message = {
            id: 'joinRoom',
            name: this.name,
            room: this.room,
            mimeType: this.getMimeType(),
        };
        this.sendMessage(message);
    }
    onNewParticipant(msg) {
        logger_1.logger.info(`[ARaaS] New Participant arrived: ${msg.name}`);
        this.receiveVideo(msg.name);
    }
    /**
     Get the correct mime type based on media constraints
     **/
    getMimeType() {
        const audio = this.isAudioLive;
        const video = this.isVideoLive;
        if (audio && video) {
            return 'AUDIO_VIDEO';
        }
        else if (audio && !video) {
            return 'AUDIO_ONLY';
        }
        else if (!audio && video) {
            return 'VIDEO_ONLY';
        }
        else {
            logger_1.logger.warn('[ARaaS Room] no valid media stream available');
            return 'NO_MEDIA';
        }
    }
    parseAndConfigureIceServers(iceServer) {
        const stun = {};
        const turn = {};
        stun.urls = iceServer.urls;
        if (stun.urls.length > 0) {
            logger_1.logger.info('stun server present in the ice server list');
        }
        turn.urls = iceServer.turnCredentials.uris;
        if (turn.urls.length > 0) {
            logger_1.logger.info('turn server present in the ice server list');
        }
        turn.credential = iceServer.turnCredentials.credential;
        turn.username = iceServer.turnCredentials.username;
        this.iceServers.push(stun);
        this.iceServers.push(turn);
        this.config.configuration.iceServers = this.iceServers;
    }
    receiveVideoResponse(msg) {
        this.participants[msg.name].rtcPeer.processAnswer(msg.sdpAnswer, (error) => {
            if (error) {
                logger_1.logger.error(`[ARaaS] Error processing SDP Answer: ${error}... ${msg.sdpAnswer}`);
            }
            else {
                logger_1.logger.info(`[ARaaS] Successfully processed SDP Answer from: ${msg.name}... media should begin flowing soon`);
            }
        });
    }
    removeSuspendedTracks(parseStream) {
        this.isAudioLive = false;
        this.isVideoLive = false;
        for (let audioTrack of parseStream.getAudioTracks()) {
            logger_1.logger.info(`[ARaaS] audio track with steady state: ${audioTrack.readyState}`);
            if (audioTrack.readyState === 'live') {
                this.isAudioLive = true;
            }
            else {
                parseStream.removeTrack(audioTrack);
                logger_1.logger.info(`[ARaaS] removed audio track with steady state: ${audioTrack.readyState}`);
            }
        }
        for (let videoTrack of parseStream.getVideoTracks()) {
            logger_1.logger.info(`[ARaaS] audio track with steady state: ${videoTrack.readyState}`);
            if (videoTrack.readyState === 'live') {
                this.isVideoLive = true;
            }
            else {
                parseStream.removeTrack(videoTrack);
                logger_1.logger.info(`[ARaaS] removed audio track with steady state: ${videoTrack.readyState}`);
            }
        }
        return parseStream;
    }
    initLocalAndExistingParticipants(msg) {
        // Configure the current participant
        logger_1.logger.info(`[ARaaS] Successfully registered user: ${this.name} in room: ${this.room} (existing participants: ${msg.data.length})`);
        this.messageHandler({ id: 'createVideoElement', name: this.name });
        const participant = new participant_1.Participant(this.name, this.room, this.ws, this.messageHandler);
        this.participants[this.name] = participant;
        // Ask client code to provide a video element for this participant
        this.messageHandler({ id: 'passDownElements', name: this.name });
        const video = participant.videoElement;
        if (!video) {
            logger_1.logger.error(`[ARaaS] Didn't receive a video element for participant: ${this.name}.  Ensure that 'passDownElements' correctly provided a video element.`);
            return;
        }
        const config = this.config;
        // parse for audio tracks, find if audio readyState is not Live, then remove that track, make contrains video only
        const options = {
            logger: logger_1.logger,
            name: config.userName,
            videoStream: this.videoStream,
            audioStream: this.audioStream,
            onTrack: config.onTrack,
            localVideo: video,
            mediaConstraints: config.mediaConstraints,
            onicecandidate: participant.onIceCandidate.bind(participant),
            dataChannels: config.dataChannels,
            dataChannelConfig: config.dataChannelConfig,
            configuration: config.configuration,
            preferredAudioCodec: config.preferredAudioCodec,
            preferredVideoCodec: config.preferredVideoCodec,
        };
        logger_1.logger.info(`[ARaaS] Initializing WebRtcPeerSendonly...`);
        participant.rtcPeer = new webrtc_peer_1.WebRtcPeer.WebRtcPeerSendonly(options, function (error) {
            if (error) {
                logger_1.logger.error(`[ARaaS] Error initializing WebRtcPeerSendonly`, error);
            }
            else {
                logger_1.logger.info(`[ARaaS] Successfully initialized WebRtcPeerSendonly... generating offer to send to peer`);
                this.generateOffer((error, sdp) => participant.offerToReceiveVideo(error, sdp));
            }
        });
        // Now setup each existing participant
        msg.data.forEach(p => {
            this.receiveVideo(p);
        });
    }
    leaveRoom() {
        logger_1.logger.info(`[ARaaS] Leaving room...`);
        this.sendMessage({
            id: 'leaveRoom',
        }, false);
        for (const participant of Object.values(this.participants)) {
            try {
                participant.dispose();
            }
            catch (error) {
                console.error('[ARaaS] Could not dispose participant: ', error);
            }
        }
        this.ws.close();
    }
    dataChannelSend(message) {
        const participant = this.participants[this.config.userName];
        if (typeof message === 'string') {
            logger_1.logger.info(`[ARaaS] Sending string Data Channel message: ${message}`);
            participant.rtcPeer.send(message);
        }
        else {
            // When sending binary messages, we currently have an issue where they are coming through as strings
            // on the other side.  If the bytes are not valid UTF-8 characters, then this results in the messages not
            // being received, since it can't be decoded to a string.  To avoid this for the time being, we are going
            // to encode binary messages as base64 strings, with the null-byte as the first character... -SA
            let data = new Uint8Array(message); // 🤦 a Uint8Array is a number[]
            let base64EncodedMessage = btoa(String.fromCharCode.apply(null, data));
            participant.rtcPeer.send('\u0000' + base64EncodedMessage);
        }
    }
    wrapDataTrackOnMessage(config) {
        var _a;
        const existingDataChannelOnMessage = (_a = config.dataChannelConfig) === null || _a === void 0 ? void 0 : _a.onmessage;
        if (!existingDataChannelOnMessage) {
            return config;
        }
        // As mentioned above, we cannot seem to receive binary messages on web, as they always come through as strings.
        // Here we wrap the onmessage handler, and check the string message for the null-byte as the first character.
        // If that is the case, we consider the remaining characters as a UTF-8 string, then base64 decode that string,
        // and provide the UInt8Array to the wrapped onmessage handler.  If we do happen to receive a binary message,
        // I guess the bug is fixed, and we can just pass that along (stilled wrapped in UInt8Array for consistency)... -SA
        const wrappedHandler = function (message) {
            const data = message.data;
            try {
                if (typeof data === 'string' && data.charAt(0) === '\u0000') {
                    const base64DecodedData = new Uint8Array(atob(data.substr(1))
                        .split('')
                        .map((c) => c.charCodeAt(0)));
                    existingDataChannelOnMessage.call(this, Object.assign(Object.assign({}, message), { data: base64DecodedData }));
                }
                else {
                    existingDataChannelOnMessage.call(this, message);
                }
            }
            catch (error) {
                logger_1.logger.warn('Failed to decode datatrack message', data, error);
            }
        };
        return Object.assign(Object.assign({}, config), { dataChannelConfig: Object.assign(Object.assign({}, config.dataChannelConfig), { onmessage: wrappedHandler }) });
    }
    receiveVideo(sender) {
        // Initialize this participant
        logger_1.logger.info(`[ARaaS] Initializing media from participant: ${sender}`);
        this.messageHandler({ id: 'createVideoElement', name: sender });
        const participant = new participant_1.Participant(sender, this.room, this.ws, this.messageHandler);
        this.participants[sender] = participant;
        // Ask client code to provide a video element for this participant
        this.messageHandler({ id: 'passDownElements', name: sender });
        const video = participant.videoElement;
        if (!video) {
            logger_1.logger.error(`[ARaaS] Didn't receive a video element for participant: ${sender}.  Ensure that 'passDownElements' correctly provided a video element.`);
            return;
        }
        participant.firstFrameLatencyInMs = Date.now();
        const config = this.config;
        const options = {
            logger: logger_1.logger,
            // not putting audiosource and videosource here - those will have to be recieved after connection made
            name: config.userName,
            participantName: sender,
            remoteVideo: video,
            onTrack: config.onTrack,
            onicecandidate: participant.onIceCandidate.bind(participant),
            onfirstFrameAvailable: participant.onFirstFrameAvailable.bind(participant),
            dataChannels: config.dataChannels,
            dataChannelConfig: config.dataChannelConfig,
            configuration: config.configuration,
            preferredAudioCodec: config.preferredAudioCodec,
            preferredVideoCodec: config.preferredVideoCodec,
        };
        logger_1.logger.info(`[ARaaS] Initializing WebRtcPeerRecvonly...`);
        participant.rtcPeer = new webrtc_peer_1.WebRtcPeer.WebRtcPeerRecvonly(options, function (error) {
            if (error) {
                logger_1.logger.error(`[ARaaS] Error initializing WebRtcPeerRecvonly`, error);
            }
            else {
                logger_1.logger.info(`[ARaaS] Successfully initialized WebRtcPeerRecvonly... generating offer to send to peer`);
                this.generateOffer((error, sdp) => participant.offerToReceiveVideo(error, sdp));
            }
        });
        this.messageHandler({ id: 'participantAdded', name: sender });
    }
    onParticipantLeft(msg) {
        logger_1.logger.info(`[ARaaS] Participant left: ${msg.name}`);
        const participant = this.participants[msg.name];
        if (participant) {
            participant.dispose();
        }
        this.messageHandler({ id: 'participantLeft', name: msg.name });
        delete this.participants[msg.name];
    }
    sendMessage(message, waitForSocket = true) {
        const jsonMessage = JSON.stringify(message);
        logger_1.logger.debug('[ARaaS] Sending message: ' + jsonMessage);
        if (waitForSocket) {
            this.waitForSocketConnection(this.ws, () => {
                this.ws.send(jsonMessage);
            });
        }
        else {
            this.ws.send(jsonMessage);
        }
    }
    passDownElements(payload) {
        logger_1.logger.info(`[ARaaS] Received HTML elements for: ${payload.name}`);
        const thisParticipant = this.participants[payload.name];
        thisParticipant.videoElement = payload.videoElement;
    }
    updateTrackForParticipant(participantId, newMediaStreamTrack) {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info(`[ARaaS] Replacing MediaStreamTrack for ${participantId} with: `, newMediaStreamTrack);
            const thisParticipant = this.participants[participantId];
            const senders = thisParticipant.rtcPeer.peerConnection.getSenders();
            const videoSenders = senders.filter(sender => sender.track.kind === 'video');
            for (const stream of videoSenders) {
                yield stream.replaceTrack(newMediaStreamTrack);
            }
        });
    }
    getStats() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let stats = [];
            for (const p of Object.values(this.participants)) {
                const stat = yield ((_a = p.rtcPeer) === null || _a === void 0 ? void 0 : _a.getPCStats());
                stat.name = p.name;
                stats.push(stat);
            }
            return stats;
        });
    }
}
exports.ConferenceRoom = ConferenceRoom;
//# sourceMappingURL=conference_room.js.map